import { useState } from 'react';
import emailjs from '@emailjs/browser';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaCheckCircle, FaExclamationCircle, FaSpinner, FaPaperPlane } from 'react-icons/fa';

const SectionWrapper = styled.section`
  padding: 6rem 1.5rem;
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-subtle);
  width: 100%;
  position: relative;

  @media (max-width: 480px) {
    padding: 3.5rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

const SectionTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.875rem;
  color: var(--accent-cyan);
  background: rgba(6, 182, 212, 0.1);
  border: 1px solid rgba(6, 182, 212, 0.2);
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  display: inline-block;
  margin-bottom: 0.85rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.35rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.5px;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.85rem;
  }

  @media (max-width: 480px) {
    font-size: 1.55rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 3rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const InfoCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  min-width: 0;

  @media (max-width: 480px) {
    padding: 1.5rem;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-primary);
    margin-bottom: 1rem;
    font-weight: 700;
  }

  p.description {
    color: var(--text-muted);
    font-size: 0.975rem;
    line-height: 1.7;
    margin-bottom: 2rem;
  }
`;

const InfoItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  .icon-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(6, 182, 212, 0.12);
    border: 1px solid rgba(6, 182, 212, 0.2);
    color: var(--accent-cyan);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;

    span.label {
      font-size: 0.8rem;
      font-family: var(--font-mono);
      color: var(--text-dim);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    a, span.value {
      color: var(--text-primary);
      text-decoration: none;
      font-size: 0.975rem;
      font-weight: 500;
      transition: color 0.2s;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    a:hover {
      color: var(--accent-cyan);
    }
  }
`;

const AvailabilityNote = styled.div`
  margin-top: 2rem;
  padding: 1rem 1.25rem;
  background: var(--bg-tertiary);
  border-radius: 12px;
  border-left: 3px solid var(--accent-teal);
  font-size: 0.875rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #10b981;
    box-shadow: 0 0 8px #10b981;
    animation: blink 2s infinite;
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

const FormCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(8px);
  min-width: 0;

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.825rem;
  font-family: var(--font-mono);
  color: var(--text-muted);
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  background: var(--bg-secondary);
  border: 1px solid ${props => (props.$hasError ? '#ef4444' : 'var(--border-subtle)')};
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: var(--font-sans);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-cyan);
    box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.15);
  }

  &::placeholder {
    color: var(--text-dim);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 0.85rem 1rem;
  background: var(--bg-secondary);
  border: 1px solid ${props => (props.$hasError ? '#ef4444' : 'var(--border-subtle)')};
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: var(--font-sans);
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-cyan);
    box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.15);
  }

  &::placeholder {
    color: var(--text-dim);
  }
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 0.775rem;
  font-family: var(--font-mono);
`;

const SubmitBtn = styled.button`
  padding: 0.9rem 1.75rem;
  background: var(--btn-primary-bg, #2563eb);
  color: var(--btn-primary-text, #ffffff);
  border: 1px solid var(--btn-primary-border, rgba(255, 255, 255, 0.15));
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: all 0.25s ease;
  box-shadow: var(--btn-primary-shadow, 0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(37, 99, 235, 0.25));
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--btn-primary-hover, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: var(--btn-primary-shadow-hover, 0 2px 6px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(37, 99, 235, 0.35));
  }

  &:active:not(:disabled) {
    background: var(--btn-primary-active, #1e40af);
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const StatusBanner = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 10px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  background: ${props => (props.$success ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)')};
  border: 1px solid ${props => (props.$success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)')};
  color: ${props => (props.$success ? '#10b981' : '#ef4444')};
`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ loading: false, success: null, message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Please provide your name';
    if (!formData.email.trim()) {
      errs.email = 'Please provide your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Please provide a valid email address';
    }
    if (!formData.message.trim()) {
      errs.message = 'Please include a message';
    } else if (formData.message.trim().length < 10) {
      errs.message = 'Message must be at least 10 characters long';
    }
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus({ loading: true, success: null, message: '' });

    const templateParams = {
      ...formData,
      to_email: 'kushwahanimish@gmail.com',
      reply_to: formData.email,
    };

    emailjs
      .send('service_hdoim79', 'template_dkral8h', templateParams, '7VyP-jeYbVsIYSqHF')
      .then(
        () => {
          setStatus({
            loading: false,
            success: true,
            message: 'Thank you! Your message has been sent successfully. I will get back to you soon.',
          });
          setFormData({ name: '', email: '', message: '' });
          setTimeout(() => {
            setStatus({ loading: false, success: null, message: '' });
          }, 6000);
        },
        () => {
          setStatus({
            loading: false,
            success: false,
            message: 'Unable to send message via automated server. Please email directly at kushwahanimish@gmail.com.',
          });
        }
      );
  };

  return (
    <SectionWrapper id="contact">
      <Container>
        <SectionHeader>
          <SectionTag>06. // contact</SectionTag>
          <SectionTitle>Let's Start a Conversation</SectionTitle>
          <SectionSubtitle>
            Have a project in mind, research collaboration, or looking to hire? I'd love to hear from you.
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid>
          <InfoCard>
            <div>
              <h3>Get In Touch</h3>
              <p className="description">
                Feel free to reach out directly through email, phone, or by filling out this form. I'm always open to discussing new opportunities, open-source initiatives, or innovative tech projects.
              </p>

              <InfoItemsList>
                <InfoItem>
                  <div className="icon-wrapper">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="details">
                    <span className="label">Location</span>
                    <span className="value">Sitapaila, Nagarjun, Kathmandu, Nepal</span>
                  </div>
                </InfoItem>

                <InfoItem>
                  <div className="icon-wrapper">
                    <FaEnvelope />
                  </div>
                  <div className="details">
                    <span className="label">Email</span>
                    <a href="mailto:kushwahanimish@gmail.com">kushwahanimish@gmail.com</a>
                  </div>
                </InfoItem>

                <InfoItem>
                  <div className="icon-wrapper">
                    <FaPhone />
                  </div>
                  <div className="details">
                    <span className="label">Phone</span>
                    <a href="tel:+9779809163469">+977 9809163469</a>
                  </div>
                </InfoItem>
              </InfoItemsList>
            </div>

            <AvailabilityNote>
              <div className="dot" />
              <span>Available for full-time roles & freelance opportunities</span>
            </AvailabilityNote>
          </InfoCard>

          <FormCard>
            {status.message && (
              <StatusBanner $success={status.success}>
                {status.success ? <FaCheckCircle /> : <FaExclamationCircle />}
                <span>{status.message}</span>
              </StatusBanner>
            )}

            <Form onSubmit={handleSubmit} noValidate>
              <FormGroup>
                <Label htmlFor="contact-name">NAME</Label>
                <Input
                  id="contact-name"
                  type="text"
                  name="name"
                  placeholder="e.g. John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  $hasError={Boolean(errors.name)}
                />
                {errors.name && <ErrorText>{errors.name}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="contact-email">EMAIL</Label>
                <Input
                  id="contact-email"
                  type="email"
                  name="email"
                  placeholder="e.g. john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  $hasError={Boolean(errors.email)}
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="contact-message">MESSAGE</Label>
                <TextArea
                  id="contact-message"
                  name="message"
                  placeholder="Tell me about your project, idea, or inquiry..."
                  value={formData.message}
                  onChange={handleChange}
                  $hasError={Boolean(errors.message)}
                />
                {errors.message && <ErrorText>{errors.message}</ErrorText>}
              </FormGroup>

              <SubmitBtn type="submit" disabled={status.loading}>
                {status.loading ? (
                  <>
                    <FaSpinner className="spin" style={{ animation: 'spin 1s linear infinite' }} /> Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Send Message
                  </>
                )}
              </SubmitBtn>
            </Form>
          </FormCard>
        </ContactGrid>
      </Container>
    </SectionWrapper>
  );
}
